<template>
  <div class="modal fade" id="modalFinalizarEjecucion" tabindex="-1" ref="modalEl">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content divisan-modal border-0">
        <div class="modal-header border-0 pb-0">
          <h5 class="fw-bold text-cafe">
            <i class="bi bi-check-circle me-2"></i>Cerrar registro de mantenimiento
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <form @submit.prevent="guardar">
          <div class="modal-body">
            <!-- Resumen de progreso -->
            <div class="resumen-final mb-4">
              <div class="row g-0 text-center">
                <div class="col-4 resumen-item">
                  <div class="resumen-num text-verde">
                    {{ store.progresoActividades.completadas }}
                  </div>
                  <div class="resumen-label">Completadas</div>
                </div>
                <div class="col-4 resumen-item border-start border-end">
                  <div class="resumen-num text-cafe">
                    {{ store.progresoActividades.total }}
                  </div>
                  <div class="resumen-label">Total</div>
                </div>
                <div class="col-4 resumen-item">
                  <div
                    class="resumen-num"
                    :style="{
                      color:
                        store.progresoActividades.pct === 100 ? 'var(--d-green)' : '#d48804',
                    }"
                  >
                    {{ store.progresoActividades.pct }}%
                  </div>
                  <div class="resumen-label">Progreso</div>
                </div>
              </div>
            </div>

            <!-- ── Actividades pendientes (si las hay) ── -->
            <template v-if="pendientes > 0">
              <div class="alerta-warning mb-3">
                <i class="bi bi-exclamation-triangle-fill me-2 flex-shrink-0"></i>
                <span>
                  Quedan <strong>{{ pendientes }}</strong> actividad(es) sin completar. La
                  rutina se cerrará en estado <strong>En proceso</strong>. Indica el
                  motivo y una fecha de reprogramación para cada una.
                </span>
              </div>

              <div class="pendientes-lista mb-4">
                <div
                  v-for="act in actividadesPendientes"
                  :key="act.id"
                  class="pendiente-item"
                >
                  <div class="d-flex align-items-center gap-2 mb-2">
                    <span class="orden-dot-sm">{{ act.orden }}</span>
                    <span class="fw-bold text-cafe small">{{
                      act.actividad_nombre
                    }}</span>
                  </div>
                  <div class="row g-2">
                    <div class="col-12 col-sm-7">
                      <label class="label-divisan-sm">Motivo de pendiente</label>
                      <input
                        v-model="form.pendientesInfo[act.id].motivo"
                        type="text"
                        class="form-control divisan-input-modal"
                        placeholder="Ej. Falta de refacción, tiempo insuficiente..."
                        required
                      />
                    </div>
                    <div class="col-12 col-sm-5">
                      <label class="label-divisan-sm">
                        Reprogramar para
                        <span class="text-cafe opacity-50">(dentro del período)</span>
                      </label>
                      <input
                        v-model="form.pendientesInfo[act.id].nueva_fecha"
                        type="date"
                        class="form-control divisan-input-modal"
                        :min="minFecha"
                        :max="maxFecha"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div class="modal-footer border-0 pt-0">
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              data-bs-dismiss="modal"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="btn btn-finalizar py-2 px-4"
              :disabled="store.guardando || !formValido"
            >
              <span
                v-if="store.guardando"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              <i v-else class="bi bi-check-lg me-1"></i>
              {{ pendientes > 0 ? "Confirmar (con pendientes)" : "Confirmar registro" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useEjecucionStore } from "@/store/ejecucionStore";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";

const emit = defineEmits(["finalizado"]);
const store = useEjecucionStore();
const modalEl = ref(null);
let modalInstance = null;

const hoy = new Date().toISOString().slice(0, 10);

// Min: fecha_programada del calendario o hoy (lo que sea mayor)
// Max: fecha_limite — fin del periodo segun periodicidad de la rutina
const minFecha = computed(() => {
  const fp = store.calendarioActivo?.fecha_programada;
  return fp && fp > hoy ? fp : hoy;
});
const maxFecha = computed(() => store.calendarioActivo?.fecha_limite || "");

const form = ref({
  pendientesInfo: {}, // { [act.id]: { motivo, nueva_fecha } }
});

const pendientes = computed(
  () => store.progresoActividades.total - store.progresoActividades.completadas
);

const actividadesPendientes = computed(() =>
  [...(store.ejecucion?.actividades || [])]
    .filter((a) => !a.completado)
    .sort((a, b) => a.orden - b.orden)
);

/**
 * Sincroniza pendientesInfo con las actividades pendientes actuales.
 * Esto garantiza que form.pendientesInfo[act.id] siempre exista
 * antes de que el template intente leerlo, incluso si el modal
 * aún no fue abierto con abrir().
 */
watch(
  actividadesPendientes,
  (actividades) => {
    const infoActual = form.value.pendientesInfo;
    const nuevoInfo = {};
    actividades.forEach((act) => {
      // Preservar lo que el usuario ya escribió si la actividad ya tenía entrada
      nuevoInfo[act.id] = infoActual[act.id] ?? { motivo: "", nueva_fecha: "" };
    });
    form.value.pendientesInfo = nuevoInfo;
  },
  { immediate: true }
);

const formValido = computed(() => {
  if (pendientes.value === 0) return true;
  const limite = maxFecha.value;
  const minimo = minFecha.value;
  return actividadesPendientes.value.every((act) => {
    const info = form.value.pendientesInfo[act.id];
    if (!info?.motivo?.trim() || !info?.nueva_fecha) return false;
    if (limite && info.nueva_fecha > limite) return false;
    if (minimo && info.nueva_fecha < minimo) return false;
    return true;
  });
});

function abrir() {
  modalInstance?.show();
}

async function guardar() {
  try {
    await store.finalizarEjecucion({
      notas: "",
      tiempoHoras: null,
      pendientesInfo: form.value.pendientesInfo,
      tienePendientes: pendientes.value > 0,
    });
    modalInstance.hide();
    emit("finalizado");
    Swal.fire({
      icon: "success",
      title:
        pendientes.value > 0
          ? "¡Registro cerrado con pendientes!"
          : "¡Mantenimiento registrado!",
      html:
        pendientes.value > 0
          ? `<p>La rutina quedó en estado <strong>En proceso</strong>. Las actividades pendientes fueron reprogramadas.</p>`
          : `<p>El registro ha sido cerrado exitosamente.</p>`,
      confirmButtonColor: "var(--d-brown)",
    });
  } catch {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudo cerrar el registro. Intenta de nuevo.",
      confirmButtonColor: "var(--d-brown)",
    });
  }
}

onMounted(() => {
  modalInstance = new Modal(modalEl.value);
});
defineExpose({ abrir });
</script>

<style scoped>
.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.label-divisan-sm {
  color: var(--d-brown);
  font-weight: 700;
  font-size: 0.78rem;
  margin-bottom: 3px;
  display: block;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
}

.alerta-warning {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 0.6rem 0.9rem;
  font-size: 0.85rem;
  color: #7a5a00;
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.resumen-final {
  background: rgba(96, 108, 56, 0.08);
  border: 1px solid rgba(96, 108, 56, 0.2);
  border-radius: 10px;
  overflow: hidden;
}
.resumen-item {
  padding: 0.75rem 0.5rem;
  border-color: rgba(96, 108, 56, 0.2) !important;
}
.resumen-num {
  font-size: 1.6rem;
  font-weight: 800;
  line-height: 1;
}
.resumen-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--d-green);
  margin-top: 2px;
}

.pendientes-lista {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.pendiente-item {
  background: white;
  border: 1px solid rgba(255, 193, 7, 0.4);
  border-left: 3px solid #ffc107;
  border-radius: 8px;
  padding: 0.75rem;
}
.orden-dot-sm {
  background: #d48804;
  color: white;
  font-size: 0.62rem;
  font-weight: 800;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.btn-finalizar {
  background-color: var(--d-green);
  color: var(--d-cream);
  border: none;
  font-weight: 700;
  border-radius: 8px;
}
.btn-finalizar:hover:not(:disabled) {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-finalizar:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
